import Sidebar from '@/components/layout/sidebar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, Edit, Repeat, Square, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const recurringTaskFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  recurrenceFrequency: z.enum(['daily', 'monthly', 'yearly']),
  recurrenceEndDate: z.string().optional(),
});

type RecurringTaskFormData = z.infer<typeof recurringTaskFormSchema>;

interface RecurringTask {
  id: string;
  title: string;
  description?: string;
  recurrenceFrequency: 'daily' | 'monthly' | 'yearly';
  recurrenceNextRun: string;
  recurrenceEndDate?: string;
  status: string;
  createdAt: string;
  createdBy: string;
  dueDate: string;
  assignedTo?: string;
  projectId?: string;
  teamId?: string;
  priority: number;
}

export default function RecurringTasks() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [editingTask, setEditingTask] = useState<RecurringTask | null>(null);
  const [deletingTask, setDeletingTask] = useState<RecurringTask | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const form = useForm<RecurringTaskFormData>({
    resolver: zodResolver(recurringTaskFormSchema),
    defaultValues: {
      title: '',
      description: '',
      recurrenceFrequency: 'daily',
      recurrenceEndDate: '',
    },
  });

  // Fetch all recurring tasks (admin can see all, others see their own)
  const {
    data: recurringTasks = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['/api/recurring-tasks'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/recurring-tasks');
      console.log('Recurring tasks API response:', response);
      console.log('User role:', user?.role);
      console.log('User ID:', user?.id);
      console.log('Response type:', typeof response);

      // Parse the JSON response
      const data = await response.json();
      console.log('Parsed JSON data:', data);
      console.log('Data type:', typeof data);
      console.log('Is array:', Array.isArray(data));

      if (Array.isArray(data)) {
        console.log('Data length:', data.length);
        console.log('First item:', data[0]);
      }

      return Array.isArray(data) ? (data as RecurringTask[]) : [];
    },
  });

  // Update recurring task mutation
  const updateRecurringMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: RecurringTaskFormData;
    }) => {
      const response = await apiRequest(
        'PATCH',
        `/api/recurring-tasks/${id}`,
        data
      );
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Recurring Task Updated',
        description: 'The recurring task has been updated successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/recurring-tasks'] });
      setIsEditModalOpen(false);
      setEditingTask(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update recurring task',
        variant: 'destructive',
      });
    },
  });

  // Delete recurring task mutation
  const deleteRecurringMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await apiRequest(
        'DELETE',
        `/api/recurring-tasks/${taskId}`
      );
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Recurring Task Deleted',
        description: 'The recurring task has been deleted successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/recurring-tasks'] });
      setIsDeleteModalOpen(false);
      setDeletingTask(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete recurring task',
        variant: 'destructive',
      });
    },
  });

  const handleEdit = (task: RecurringTask) => {
    setEditingTask(task);
    form.reset({
      title: task.title,
      description: task.description || '',
      recurrenceFrequency: task.recurrenceFrequency,
      recurrenceEndDate: task.recurrenceEndDate
        ? new Date(task.recurrenceEndDate).toISOString().split('T')[0]
        : '',
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (task: RecurringTask) => {
    setDeletingTask(task);
    setIsDeleteModalOpen(true);
  };

  const onSubmitEdit = (data: RecurringTaskFormData) => {
    if (editingTask) {
      updateRecurringMutation.mutate({ id: editingTask.id, data });
    }
  };

  const onConfirmDelete = () => {
    if (deletingTask) {
      deleteRecurringMutation.mutate(deletingTask.id);
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
      default:
        return frequency;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canManageTask = (task: RecurringTask) => {
    return user?.role === 'admin' || task.createdBy === user?.id;
  };

  if (authLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Repeat className="w-6 h-6" />
              Manage Recurring Tasks
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage your recurring tasks. You can edit or delete any recurring
              task you created.
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">
                Loading recurring tasks...
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-red-500">
                Error loading recurring tasks: {error.message}
              </div>
            </div>
          ) : recurringTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Square className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                No Recurring Tasks
              </h3>
              <p className="text-gray-500 max-w-md">
                You haven't created any recurring tasks yet. Create a task and
                enable recurrence to see it here.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {recurringTasks.map(task => (
                <Card
                  key={task.id}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {task.title}
                          <Badge
                            variant="outline"
                            className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-700"
                          >
                            <Repeat className="w-3 h-3 mr-1" />
                            {getFrequencyLabel(task.recurrenceFrequency)}
                          </Badge>
                        </CardTitle>
                        {task.description && (
                          <CardDescription className="mt-2">
                            {task.description}
                          </CardDescription>
                        )}
                      </div>
                      {canManageTask(task) && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(task)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(task)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500">Next Run</Label>
                        <div className="font-medium flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDateTime(task.recurrenceNextRun)}
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Due Date</Label>
                        <div className="font-medium flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDateTime(task.dueDate)}
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Priority</Label>
                        <div className="font-medium">{task.priority || 0}</div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Status</Label>
                        <div className="font-medium">
                          <Badge variant="outline">{task.status}</Badge>
                        </div>
                      </div>
                    </div>
                    {task.recurrenceEndDate && (
                      <div className="mt-4 pt-4 border-t">
                        <Label className="text-gray-500">Ends On</Label>
                        <div className="font-medium">
                          {formatDateTime(task.recurrenceEndDate)}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Recurring Task Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Recurring Task</DialogTitle>
            <DialogDescription>
              Update the details of this recurring task.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit(onSubmitEdit)}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                {...form.register('title')}
                placeholder="Enter task title"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...form.register('description')}
                placeholder="Enter task description (optional)"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="recurrenceFrequency">Recurrence Frequency</Label>
              <Select
                value={form.watch('recurrenceFrequency')}
                onValueChange={value =>
                  form.setValue('recurrenceFrequency', value as any)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="recurrenceEndDate">End Date (Optional)</Label>
              <Input
                id="recurrenceEndDate"
                type="date"
                {...form.register('recurrenceEndDate')}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateRecurringMutation.isPending}
              >
                {updateRecurringMutation.isPending
                  ? 'Updating...'
                  : 'Update Task'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Recurring Task</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this recurring task? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deletingTask && (
            <div className="py-4">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium">{deletingTask.title}</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Frequency:{' '}
                  {getFrequencyLabel(deletingTask.recurrenceFrequency)}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={deleteRecurringMutation.isPending}
            >
              {deleteRecurringMutation.isPending
                ? 'Deleting...'
                : 'Delete Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
